/* =========================================================
   AF Quick Add - the view's behaviour (sections/af-quick-add.liquid)
   Redesigned Sep 2026 (Claude Design "Quick Add and Cart Drawer"; spec
   docs/superpowers/specs/2026-09-25-pdp-redesign-design.md, pass 2).

   - size tiles are a radio group (one tab stop, arrow keys move the choice);
     a size switch rolls the price, updates sprays/wears, the Store Credit line and
     Buy it now (the price is the only figure: no size or total beside it)
   - stepper: minus dims at the minimum
   - offers bar (Store Credit mode off): basket = cart subtotal (fetched
     fresh on open) + this size x qty
   - Store Credit earn line (mode on only): basket = cart subtotal (fetched
     fresh on open) + this size x qty
   - Add to cart is Dawn's product-form.js. On success the button shows
     "ADDED ✓", the modal holds open for HOLD ms (af-quick-add-modal.js reads
     __afHoldUntil), then closes and product-form opens the cart drawer
   - pages that don't load product-form.js / af-offers.js / the odometer get
     them injected from the URLs on the view (data-*-js)
   - phones: drag the sheet's header down to close it
   ========================================================= */

(() => {
  if (window.__afQuickAddViewInit) return;
  window.__afQuickAddViewInit = true;

  const HOLD = 500; // ms the "ADDED ✓" state shows before the modal closes
  const mqPhone = window.matchMedia('(max-width: 749px)');
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // same format as snippets/price.liquid ("৳ 1,500") and af-money ("৳1,500")
  const money = (cents) => '৳ ' + Math.round(cents / 100).toLocaleString('en-US');

  let _paymentButtonsInitThisCycle = false;
  const initPaymentButtons = () => {
    if (_paymentButtonsInitThisCycle) return;
    _paymentButtonsInitThisCycle = true;
    try {
      if (window.Shopify && Shopify.PaymentButton && typeof Shopify.PaymentButton.init === 'function') {
        Shopify.PaymentButton.init();
      }
    } catch (e) {}
  };
  const resetPaymentButtonsGate = () => { _paymentButtonsInitThisCycle = false; };

  /* ---------- scripts the view needs ---------- */
  const injectScript = (src, marker) => {
    if (!src || document.querySelector(`script[data-${marker}]`)) return null;
    const sc = document.createElement('script');
    sc.src = src;
    sc.defer = true;
    sc.setAttribute(`data-${marker}`, '1');
    document.head.appendChild(sc);
    return sc;
  };

  /* The odometer (digits spin to the new price) lives in af-price-odometer.js,
     which only product pages load; fetch it once, the first time a quick-add
     opens. Until it arrives the price just swaps as plain text. */
  const ensureOdometer = (root) => {
    if (window.AFOdometer) return;
    const css = root.dataset.odoCss;
    if (css && !document.querySelector(`link[href="${css}"]`)) {
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = css;
      document.head.appendChild(l);
    }
    injectScript(root.dataset.odoJs, 'af-odo');
  };

  // Search results and the homepage grid don't load Dawn's product-form.js.
  const ensureProductForm = (root) => {
    if (customElements.get('product-form')) return;
    if (document.querySelector('script[src*="/product-form.js"]')) return; // loading already
    injectScript(root.dataset.pfJs, 'af-pf');
  };

  const ensureOffers = (root) => {
    if (window.AFOffers) return;
    const sc = injectScript(root.dataset.offersJs, 'af-offers');
    if (sc) sc.addEventListener('load', () => document.querySelectorAll('.af-qa-view').forEach(renderOffers));
  };

  // Add to cart tapped before product-form.js arrived: hold the submit, then replay it.
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('.af-qa-view')) return;
    if (customElements.get('product-form')) return;
    e.preventDefault();
    ensureProductForm(form.closest('.af-qa-view'));
    const submitter = e.submitter && e.submitter.form === form ? e.submitter : undefined;
    customElements.whenDefined('product-form').then(() => {
      if (form.isConnected) form.requestSubmit(submitter);
    });
  }, true);

  /* ---------- state ---------- */
  const getVariants = (root) => {
    if (root.__afVariants) return root.__afVariants;
    const el = root.querySelector('[data-variants-json]');
    let list = [];
    try { list = JSON.parse((el && el.textContent) || '[]'); } catch (e) {}
    root.__afVariants = list;
    return list;
  };
  const currentVariant = (root) => {
    const input = root.querySelector('[data-variant-input]');
    return getVariants(root).find((x) => String(x.id) === String(input && input.value)) || null;
  };
  const qtyInput = (root) => root.querySelector('.af-qa-qtyinput');
  const qtyMin = (input) => Math.max(1, parseInt(input.min, 10) || 1);
  const qtyMax = (input) => Math.max(qtyMin(input), parseInt(input.max, 10) || 99);
  const qtyStep = (input) => Math.max(1, parseInt(input.step, 10) || 1);
  const getQty = (root) => {
    const input = qtyInput(root);
    if (!input) return 1;
    const n = parseInt(input.value, 10);
    return Math.min(qtyMax(input), Math.max(qtyMin(input), Number.isFinite(n) ? n : qtyMin(input)));
  };

  /* ---------- price ---------- */
  const updatePrice = (root, v) => {
    const wrap = root.querySelector('[data-price-wrap]');
    if (!wrap || !v) return;

    const compare = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : null;
    const text = money(v.price);
    const prevText = wrap.dataset.afPriceNow || '';
    if (prevText === text && wrap.dataset.afCompare === String(compare || '')) return;
    wrap.dataset.afPriceNow = text;
    wrap.dataset.afCompare = String(compare || '');

    wrap.innerHTML = `
      <div class="price${compare ? ' price--on-sale' : ''}">
        <span class="price-item price-item--sale"></span>
        ${compare ? `<s class="price-item price-item--regular">${money(compare)}</s>` : ``}
      </div>
    `;
    const now = wrap.querySelector('.price-item--sale');
    const m = text.match(/^([^\d]*)([\d][\d.,]*)(.*)$/);
    const prevAmount = (prevText.match(/[\d][\d.,]*/) || [''])[0];
    if (window.AFOdometer && m && prevAmount && !reduced()) {
      if (m[1]) now.appendChild(document.createTextNode(m[1]));
      window.AFOdometer.render(now, m[2], prevAmount, () => {
        if (now.isConnected) now.textContent = text; // plain text once settled
      });
      if (m[3]) now.appendChild(document.createTextNode(m[3]));
      const sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = text;
      now.appendChild(sr);
    } else {
      now.textContent = text;
    }
  };

  // Set a number that spins like the price (odometer) when it changes.
  const odoSet = (el, value) => {
    if (!el) return;
    const next = String(value);
    const prev = el.dataset.afV || '';
    if (prev === next) return;
    el.dataset.afV = next;
    el.textContent = '';
    if (window.AFOdometer && prev && !reduced()) {
      window.AFOdometer.render(el, next, prev, () => { if (el.isConnected && el.dataset.afV === next) el.textContent = next; });
    } else {
      el.textContent = next;
    }
  };

  // "5ML Decant (75+ sprays)" -> "75+ sprays · 15+ wears" (5 sprays a wear)
  const SPRAYS_PER_WEAR = 5;
  const updateSprays = (root, v) => {
    const text = root.querySelector('[data-af-qa-sprays-text]');
    if (!text || !v) return;
    const m = String(v.title || '').match(/(\d[\d,]*)\s*\+?\s*sprays?/i);
    if (!m) { text.hidden = true; return; }
    const n = parseInt(m[1].replace(/,/g, ''), 10);
    const plus = /\+/.test(m[0]) ? '+' : '';
    const wears = Math.floor(n / SPRAYS_PER_WEAR);
    odoSet(root.querySelector('[data-af-qa-sprays-count]'), n);
    root.querySelector('[data-af-qa-sprays-plus]').textContent = plus;
    odoSet(root.querySelector('[data-af-qa-wears]'), wears);
    root.querySelector('[data-af-qa-wears-plus]').textContent = plus;
    root.querySelector('[data-af-qa-wears-word]').textContent = wears === 1 && !plus ? 'wear' : 'wears';
    text.hidden = wears < 1;
  };

  const updateAvailability = (root, v) => {
    const atc = root.querySelector('.af-qa-atc');
    if (!atc || !v) return;
    const t = atc.querySelector('[data-af-qa-atc-text]');
    const strings = window.variantStrings || {};
    atc.disabled = !v.available;
    atc.classList.remove('is-added');
    if (t) t.textContent = String(v.available ? strings.addToCart || 'Add to cart' : strings.soldOut || 'Sold out').toUpperCase();
    root.classList.toggle('is-soldout', !v.available);
  };

  /* ---------- stepper + Store Credit line ---------- */
  const syncStepper = (root) => {
    const input = qtyInput(root);
    const minus = root.querySelector('[data-qty="minus"]');
    const plus = root.querySelector('[data-qty="plus"]');
    if (input && minus) {
      const q = getQty(root);
      minus.classList.toggle('is-min', q <= qtyMin(input));
      minus.setAttribute('aria-disabled', q <= qtyMin(input) ? 'true' : 'false');
      if (plus) plus.setAttribute('aria-disabled', q >= qtyMax(input) ? 'true' : 'false');
    }
  };

  function renderOffers(root) {
    // Store Credit mode off: the offers bar (snippets/af-offers.liquid)
    const bar = root.querySelector('[data-af-qa-offers] .af-offers');
    if (bar) {
      const v = currentVariant(root);
      if (!v || !window.AFOffers) return;
      const basket = (Number(root.dataset.cartSubtotal) || 0) + v.price * getQty(root);
      window.AFOffers.render(bar, basket);
      return;
    }
    // Store Credit mode on: the earn line (snippets/af-cashback-earn.liquid); off / shadow: not rendered
    const earnLine = root.querySelector('[data-af-qa-offers] [data-af-cb-earn]');
    if (!earnLine) return;
    const ev = currentVariant(root);
    if (!ev || !window.AFCashback) return;
    // seeded from the page (Liquid) until the live cart fetch lands, so the first render agrees with the first paint
    if (!root.__afInCart) root.__afInCart = window.AFCashback.parseInCart(root.dataset.inCart);
    const inCart = root.__afInCart[ev.id] || 0;
    const total = window.AFCashback.earnTotal(Number(root.dataset.cartSubtotal) || 0, ev.price, getQty(root), inCart);
    window.AFCashback.renderEarn(earnLine, total, !ev.available, root.__afDiscounted);
  }

  // The view may be up to a minute old (prefetch cache): take the subtotal from the live cart.
  const refreshSubtotal = (root) => {
    const url = ((window.routes && window.routes.cart_url) || '/cart') + '.js';
    fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart || !root.isConnected) return;
        root.dataset.cartSubtotal = String(cart.items_subtotal_price || 0);
        const inCart = {};
        (cart.items || []).forEach((it) => { inCart[it.variant_id] = (inCart[it.variant_id] || 0) + it.quantity; });
        root.__afInCart = inCart;
        if (window.AFCashback) root.__afDiscounted = window.AFCashback.hasOtherDiscount(cart);
        renderOffers(root);
      })
      .catch(() => {});
  };

  /* ---------- size ---------- */
  const setActive = (root, variantId) => {
    root.querySelectorAll('.af-qa-size[data-variant-id]').forEach((b) => {
      const on = String(b.dataset.variantId) === String(variantId);
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.tabIndex = on ? 0 : -1;
    });
  };

  const setVariant = (root, variantId) => {
    const v = getVariants(root).find((x) => String(x.id) === String(variantId));
    if (!v) return;

    const variantInput = root.querySelector('[data-variant-input]');
    const changed = variantInput && variantInput.value !== String(v.id);
    if (variantInput) {
      variantInput.value = String(v.id);
      if (changed) {
        variantInput.dispatchEvent(new Event('input', { bubbles: true }));
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    setActive(root, v.id);
    updatePrice(root, v);
    updateSprays(root, v);
    updateAvailability(root, v);
    syncStepper(root);
    renderOffers(root);

    // Re-init dynamic checkout so Buy it now tracks the selected size
    requestAnimationFrame(() => initPaymentButtons());
  };

  const initRoot = (root) => {
    if (!root || root.dataset.afQaInit === '1') return;
    root.dataset.afQaInit = '1';
    ensureOdometer(root);
    ensureProductForm(root);
    if (root.dataset.offersJs) ensureOffers(root); // mode off only: the view carries the URL

    const variantInput = root.querySelector('[data-variant-input]');
    const variants = getVariants(root);
    const initialId = (variantInput && variantInput.value) || (variants[0] && variants[0].id);
    if (initialId) setVariant(root, initialId);

    initPaymentButtons();
    refreshSubtotal(root);
  };

  // When the modal injects a new Quick Add view, initialize only that view (no full rescans)
  document.addEventListener('af:quickadd:loaded', (e) => {
    try {
      resetPaymentButtonsGate();
      const modal = e && e.target;
      if (!(modal instanceof Element)) return;
      const root = modal.querySelector('.af-qa-view');
      if (root) initRoot(root);
    } catch (err) {}
  });

  const findRoot = (node) => (node && node.closest ? node.closest('.af-qa-view') : null);

  const closeModalFromRoot = (root) => {
    const modal = root.closest('quick-add-modal');
    if (modal && typeof modal.hide === 'function') modal.hide(true);
    else if (modal) modal.removeAttribute('open');
  };

  /* ---------- added ---------- */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (ev) => {
      if (!ev || ev.source !== 'product-form' || !ev.cartData || ev.cartData.status) return;
      const modal = document.querySelector('quick-add-modal.af-quickadd[open]');
      const root = modal && modal.querySelector('.af-qa-view');
      if (!root) return;
      const input = root.querySelector('[data-variant-input]');
      if (!input || String(input.value) !== String(ev.productVariantId)) return;
      const atc = root.querySelector('.af-qa-atc');
      if (atc) atc.classList.add('is-added');
      refreshSubtotal(root); // the added size is now in the cart: the earn line stops counting it on top
      // product-form now hides the modal; hold it open so "ADDED ✓" is seen
      modal.__afHoldUntil = Date.now() + HOLD;
      // when the cart drawer closes, focus goes back to the card's quick add button
      const drawer = document.querySelector('cart-drawer');
      if (drawer && modal._openedBy && typeof drawer.setActiveElement === 'function') {
        drawer.setActiveElement(modal._openedBy);
      }
    });
  }

  /* ---------- clicks + keys ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    const root = findRoot(t);
    if (!root) return;

    const closeBtn = t.closest('.af-qa-close');
    if (closeBtn) {
      e.preventDefault();
      e.stopPropagation();
      closeModalFromRoot(root);
      return;
    }

    const tile = t.closest('.af-qa-size[data-variant-id]');
    if (tile) {
      if (tile.disabled) return;
      setVariant(root, tile.dataset.variantId);
      return;
    }

    const qtyBtn = t.closest('[data-qty]');
    if (qtyBtn) {
      const input = qtyInput(root);
      if (!input) return;
      const cur = getQty(root);
      const step = qtyStep(input);
      const next = qtyBtn.dataset.qty === 'plus'
        ? Math.min(qtyMax(input), cur + step)
        : Math.max(qtyMin(input), cur - step);
      if (next === cur) return;
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  // typed quantity: keep it in range, then refresh the total and Store Credit line
  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('af-qa-qtyinput')) return;
    const root = findRoot(input);
    if (!root) return;
    const q = getQty(root);
    if (input.value !== String(q)) input.value = String(q);
    syncStepper(root);
    renderOffers(root);
  });
  document.addEventListener('input', (e) => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('af-qa-qtyinput')) return;
    const root = findRoot(input);
    if (root && input.value !== '') { syncStepper(root); renderOffers(root); }
  });

  // size tiles: arrow keys move the choice (radio group), skipping sold-out sizes
  document.addEventListener('keydown', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    const tile = t && t.closest('.af-qa-view .af-qa-size');
    if (!tile) return;
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1, Home: 'first', End: 'last' };
    const k = keys[e.key];
    if (!k) return;
    const tiles = [...tile.parentElement.querySelectorAll('.af-qa-size:not(:disabled)')];
    if (!tiles.length) return;
    e.preventDefault();
    let i = tiles.indexOf(tile);
    if (k === 'first') i = 0;
    else if (k === 'last') i = tiles.length - 1;
    else i = (i + k + tiles.length) % tiles.length;
    const next = tiles[i];
    setVariant(findRoot(next), next.dataset.variantId);
    next.focus();
  });

  /* ---------- phones: drag the header down to close ---------- */
  let drag = null;
  document.addEventListener('touchstart', (e) => {
    if (!mqPhone.matches || e.touches.length !== 1) return;
    const t = e.target instanceof Element ? e.target : null;
    const handle = t && t.closest('quick-add-modal.af-quickadd[open] .af-qa-grab, quick-add-modal.af-quickadd[open] .af-qa-head');
    if (!handle || t.closest('button')) return;
    const modal = handle.closest('quick-add-modal');
    const card = modal && modal.querySelector('.quick-add-modal__content');
    if (!card) return;
    drag = { modal, card, y0: e.touches[0].clientY, t0: performance.now(), dy: 0 };
  }, { passive: true });

  document.addEventListener('touchmove', (e) => {
    if (!drag) return;
    drag.dy = Math.max(0, e.touches[0].clientY - drag.y0);
    // translate (not transform): the open animation holds transform with fill-mode both
    drag.card.style.setProperty('transition', 'none', 'important');
    drag.card.style.setProperty('translate', `0 ${drag.dy}px`, 'important');
  }, { passive: true });

  const endDrag = () => {
    if (!drag) return;
    const { modal, card, dy, t0 } = drag;
    drag = null;
    const v = dy / Math.max(1, performance.now() - t0);
    if (dy > 90 || (dy > 30 && v > 0.5)) {
      modal.hide(); // slides out from where the finger left it; translate is cleared on close
      return;
    }
    card.style.setProperty('transition', 'translate .22s cubic-bezier(.2,.8,.2,1)', 'important');
    card.style.setProperty('translate', '0 0', 'important');
    setTimeout(() => {
      card.style.removeProperty('translate');
      card.style.removeProperty('transition');
    }, 240);
  };
  document.addEventListener('touchend', endDrag, { passive: true });
  document.addEventListener('touchcancel', endDrag, { passive: true });

  // Boot now + on injected content
  const boot = () => {
    document.querySelectorAll('.af-qa-view').forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
